// Two-factor sign-in (Kyle, 2026-10-25): every Cockpit login needs an authenticator code. Supabase supplies TOTP
// (enroll / challenge / verify); the app decides what's required. TwoFactorGate keeps the whole app behind it until
// the session is aal2, and the database refuses anything below aal2 (migration 20261025140000), so skipping the
// screens doesn't help. The agents use the service key and never see any of this.

import { supabase } from './supabase';

/**
 * checking: not read yet. ok: this session passed the code step (aal2). enroll: no authenticator yet.
 * verify: an authenticator exists and this session hasn't entered a code. signed-out: no session at all.
 * error: couldn't tell; the gate fails closed and offers a retry.
 */
export type MfaState = 'checking' | 'ok' | 'enroll' | 'verify' | 'signed-out' | 'error';

export async function readMfaState(): Promise<MfaState> {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) return 'signed-out';
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error || !data) return 'error';
  if (data.currentLevel === 'aal2') return 'ok';
  return data.nextLevel === 'aal2' ? 'verify' : 'enroll';
}

/** The account's verified authenticator, if it has one. */
export async function verifiedFactorId(): Promise<string | null> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw error;
  return data?.totp?.find((f) => f.status === 'verified')?.id ?? null;
}

/** Starts enrollment: clears unfinished attempts (enroll refuses a duplicate name), then returns the QR and secret. */
export async function startEnrollment(): Promise<{ factorId: string; qr: string; secret: string }> {
  const { data: existing } = await supabase.auth.mfa.listFactors();
  for (const f of existing?.all ?? []) {
    if (f.status !== 'verified') await supabase.auth.mfa.unenroll({ factorId: f.id });
  }
  const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: `Authenticator ${new Date().toISOString().slice(0, 10)}` });
  if (error) throw error;
  return { factorId: data.id, qr: data.totp.qr_code, secret: data.totp.secret };
}

/** Checks a 6-digit code against a factor. On success the session is upgraded to aal2. */
export async function verifyCode(factorId: string, code: string): Promise<void> {
  const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({ factorId });
  if (cErr) throw cErr;
  const { error: vErr } = await supabase.auth.mfa.verify({ factorId, challengeId: challenge.id, code: code.trim() });
  if (vErr) throw vErr;
}
